import { EventEmitter, Injectable } from '@angular/core';
import { ConfigurationService } from './configuration.service';
import { PriceService } from './price.service';
import { ThreeDService } from './three-d.service';
import { ModularGoogleAnalyticsService } from './modular-google-analytics.service';
import { WallSide } from '../enums/wallSide.enum';
import { StartupService } from './startup.service';
import { debounceTime, take } from 'rxjs/operators';
import { AppInsightsService } from './appinsights.service';

@Injectable({
  providedIn: 'root',
})
export class StepService {
  public step = 1;
  public maxStep = 8;
  public minStep = 1;
  loading$: EventEmitter<boolean> = new EventEmitter<boolean>();
  blockOnDoorCount: boolean;
  private newStep$: EventEmitter<number> = new EventEmitter<number>();

  constructor(
    private appInsightsService: AppInsightsService,
    private configurationService: ConfigurationService,
    private priceService: PriceService,
    private threeDService: ThreeDService,
    private startupService: StartupService,
    private modularGoogleAnalyticsService: ModularGoogleAnalyticsService
  ) {
    this.appInsightsService.startPageTrack(this.stepName(1));
    this.modularGoogleAnalyticsService.sendPageView(this.stepDescriptionEnglish(1));
    this.startupService.loading$.subscribe(loading => this.loading$.emit(loading));
    this.startupService.moveToSummaryForLoadedConfiguration$.pipe(debounceTime(300), take(1)).subscribe(() => {
      this.actuallyMoveStep(this.maxStep);
      this.loading$.emit(false);
    });
  }

  get stepChange$() {
    return this.newStep$;
  }

  get forwardBlocked() {
    if (this.blockOnDoorCount) {
      if (
        this.configurationService.configuration.settings.doorSettings.filter(d => d.side !== WallSide.inner).length ===
        0
      ) {
        return true;
      }
    }

    if (this.priceService.priceEnabled && !this.priceService.hasPrices) {
      return true;
    }

    if (!this.threeDService.modelIsReady) {
      return true;
    }

    return false;
  }

  get forwardBlockedMessage(): string {
    if (this.blockOnDoorCount) {
      if (
        this.configurationService.configuration.settings.doorSettings.filter(d => d.side !== WallSide.inner).length ===
        0
      ) {
        return 'ERRORS.ATLEASTONEDOOR';
      }
    }
    if (this.priceService.priceEnabled && !this.priceService.hasPrices) {
      return 'ERRORS.PRICESBEINGLOADED';
    }

    if (!this.threeDService.modelIsReady) {
      return 'ERRORS.MODELBEINGLOADED';
    }

    return '';
  }

  stepName(step: number) {
    switch (step) {
      case 4:
        return 'STEP4';
      case 5:
        return 'STEPINNERWALL';
      case 6:
        return 'STEP5';
      case 7:
        return 'STEP6';
      default:
        return 'STEP' + step;
    }
  }

  stepDescriptionEnglish(step: number) {
    switch (step) {
      case 1:
        return 'Purpose';
      case 2:
        return 'WallThickness';
      case 3:
        return 'Dimensions';
      case 4:
        return 'Floor';
      case 5:
        return 'InnerWalls';
      case 6:
        return 'Doors';
      case 7:
        return 'Finishes';
      case 8:
        return 'Summary';
      default:
        return 'unknown step';
    }
  }

  blockForwardUntilMoreThanOneDoorSelected() {
    this.blockOnDoorCount = true;
  }

  releaseBlockForwardUntilMoreThanOneDoorSelected() {
    this.blockOnDoorCount = false;
  }

  nextStep(): number {
    if (this.forwardBlocked) {
      return this.step;
    }
    if (this.step + 1 <= this.maxStep) {
      this.actuallyMoveStep(this.step + 1);
    }
    return this.step;
  }

  previousStep(): number {
    if (this.step - 1 >= this.minStep) {
      this.actuallyMoveStep(this.step - 1);
    }
    return this.step;
  }

  moveToStep(stepNumber: number): number {
    if (this.forwardBlocked && stepNumber > this.step) {
      return;
    }
    if (stepNumber >= this.minStep && stepNumber <= this.maxStep) {
      this.actuallyMoveStep(stepNumber);
    }

    return this.step;
  }

  getActiveStep() {
    return this.step;
  }

  getMinStep() {
    return this.minStep;
  }

  getMaxStep() {
    return this.maxStep;
  }

  private _logStepChange(newStep: number) {
    this.appInsightsService.stopPageTrack(this.stepName(this.step), window.location.href, {
      configuration: this.configurationService.configuration.configurationId,
    });
    this.appInsightsService.startPageTrack(this.stepName(newStep));
    this.modularGoogleAnalyticsService.sendPageView(this.stepDescriptionEnglish(newStep));
  }

  private actuallyMoveStep(stepNumber: number) {
    this._logStepChange(stepNumber);
    this.step = stepNumber;
    this.newStep$.emit(this.step);
  }
}
